import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { Drawer, Button, Input, Space, Row, Col, DatePicker, InputNumber } from 'antd';
import { isMobile } from 'react-device-detect';
import BaseSelect from '@/components/Elements/BaseSelect';
import { _newLine, isValidDateForm, paymentMethods, paymentStatusOptions } from '@/utils/helpers';
import { createCustomerPayment, updateCustomerPayment } from '@/redux/actions/CustomerPayment';
import BaseRadioList from '@/components/Elements/BaseRadios';
import { getCustomerServices } from '@/redux/actions/customerServicesActions';
import moment from 'moment';

class CustomerPaymentForm extends Component {
    constructor() {
        super();
        this.state = {
            isLoading: false,
            payment_method: null,
        };
    }

    componentDidMount() {
        const { visible, customerId } = this.props;
        if (visible && customerId) {
            this.props.getCustomerServices(customerId);
        }
        this.syncStateFromCurrentItem(this.props.currentItem);
    }

    componentDidUpdate(prevProps) {
        const { visible, customerId, currentItem } = this.props;

        // mở form hoặc đổi customerId -> load services
        const justOpened = !prevProps.visible && visible;
        const changedCustomer = prevProps.customerId !== customerId;
        if (visible && customerId && (justOpened || changedCustomer)) {
            this.props.getCustomerServices(customerId);
        }

        // currentItem đổi -> sync payment_method + setFieldsValue cho chắc (antd v3)
        if (prevProps.currentItem !== currentItem) {
            this.syncStateFromCurrentItem(currentItem);

            // setFieldsValue để đảm bảo form cập nhật khi edit
            if (visible) {
                this.setFormValuesFromItem(currentItem);
            }
        }

        // đóng form -> reset
        if (prevProps.visible && !visible) {
            this.props.form.resetFields();
            this.setState({ isLoading: false, payment_method: null });
        }
    }

    // ---------- helpers ----------
    getCustomerServiceIdsFromItem = (item) => {
        // list trả về: payment_services: [{ customer_service_id, customer_service: {...}}]
        const services = item?.payment_services || [];
        return services
            .map(s => s?.customer_service_id || s?.customer_service?.id)
            .filter(Boolean);
    };

    syncStateFromCurrentItem = (item) => {
        this.setState({
            payment_method: item?.payment_method ?? null,
        });
    };

    setFormValuesFromItem = (item) => {
        const { form } = this.props;

        form.setFieldsValue({
            payment_method: item?.payment_method ?? null,
            start_at: item?.start_at ? isValidDateForm(item.start_at, false) : moment(),
            total_amount: item?.total_amount ?? null,
            installment_months: item?.installment_months ?? null,
            monthly_payment: item?.monthly_payment ?? null,
            status: item?.status ?? null,
            customer_service_ids: this.getCustomerServiceIdsFromItem(item),
            note: _newLine(item?.note) ?? null,
        });
    };

    // ---------- actions ----------
    handleChangePaymentMethod = (value) => {
        this.setState({ payment_method: value });

        // (optional) nếu bạn muốn khi đổi method thì clear các field trả góp
        // if (!["tragoptainhakhoa", "tragoptainganhang"].includes(value)) {
        //     this.props.form.setFieldsValue({
        //         total_amount: null,
        //         installment_months: null,
        //         monthly_payment: null,
        //     });
        // }
    };

    async submit(e) {
        e.preventDefault();
        this.props.form.validateFields(async (err, values) => {
            if (!err) {
                this.setState({ isLoading: true });
                try {
                    values.customer_id = this.props?.customerId;

                    // DatePicker -> string nếu backend cần
                    if (values.start_at && moment.isMoment(values.start_at)) {
                        values.start_at = values.start_at.format('YYYY-MM-DD');
                    }

                    if (this.props.currentItem) {
                        await this.props.updateCustomerPayment(this.props.currentItem?.id, values);
                    } else {
                        await this.props.createCustomerPayment(values);
                    }

                    this.props.reloadData(); // list sẽ tự fetch lại
                    this.onClose();
                } catch (error) {
                    this.setState({ isLoading: false });
                }
            }
        });
    }

    onClose() {
        this.props.onCancel();
        this.props.form.resetFields();
        this.setState({ isLoading: false, payment_method: null });
    }

    render() {
        const { isLoading, payment_method } = this.state;
        const { visible, currentItem, customer_services } = this.props;
        const { getFieldDecorator } = this.props.form;

        const isInstallment = ["tragoptainhakhoa", "tragoptainganhang"].includes(payment_method);

        // initial service ids: ưu tiên map từ payment_services (khớp list)
        const initialServiceIds = currentItem
            ? this.getCustomerServiceIdsFromItem(currentItem)
            : (currentItem?.customer_service_ids ?? []);

        return (
            <Drawer
                visible={visible}
                title={`${currentItem ? "Cập nhật" : "Thêm mới"}`}
                onOk={(e) => this.submit(e)}
                onClose={() => this.onClose()}
                maskClosable={false}
                width={isMobile ? '100%' : '50%'}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button size='large' key="back" onClick={() => this.onClose()}>
                                Huỷ
                            </Button>
                            <Button
                                loading={isLoading}
                                key="submit"
                                type="primary"
                                size='large'
                                onClick={(e) => this.submit(e)}
                            >
                                {currentItem ? "Cập nhật" : "Thêm mới"}
                            </Button>
                        </Space>
                    </div>
                }
            >
                {visible ? (
                    <Form layout='vertical'>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item label="Hình thức thanh toán">
                                    {getFieldDecorator('payment_method', {
                                        rules: [{ required: true, message: 'Vui lòng chọn!' }],
                                        initialValue: currentItem?.payment_method ?? null,
                                    })(
                                        <BaseSelect
                                            options={paymentMethods || []}
                                            optionValue="value"
                                            optionLabel="label"
                                            showSearch
                                            placeholder="Chọn một bản ghi"
                                            style={{ width: '100%' }}
                                            onChange={this.handleChangePaymentMethod}
                                        />
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày bắt đầu">
                                    {getFieldDecorator('start_at', {
                                        rules: [{ required: true, message: 'Vui lòng điền!' }],
                                        initialValue: currentItem?.start_at
                                            ? isValidDateForm(currentItem.start_at, false)
                                            : moment(),
                                    })(
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            style={{ width: '100%' }}
                                            placeholder="Ngày bắt đầu"
                                        />
                                    )}
                                </Form.Item>
                            </Col>

                            {isInstallment ? (
                                <>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Tổng số tiền">
                                            {getFieldDecorator('total_amount', {
                                                rules: [{ required: true, message: 'Vui lòng nhập!' }],
                                                initialValue: currentItem?.total_amount ?? null,
                                            })(
                                                <InputNumber
                                                    step={1000}
                                                    min={0}
                                                    className='w-100'
                                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                />
                                            )}
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Số tháng">
                                            {getFieldDecorator('installment_months', {
                                                rules: [{ required: false }],
                                                initialValue: currentItem?.installment_months ?? null,
                                            })(
                                                <InputNumber min={0} className='w-100' />
                                            )}
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Số tiền trả hàng tháng">
                                            {getFieldDecorator('monthly_payment', {
                                                rules: [{ required: false }],
                                                initialValue: currentItem?.monthly_payment ?? null,
                                            })(
                                                <InputNumber
                                                    step={1000}
                                                    min={0}
                                                    className='w-100'
                                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                />
                                            )}
                                        </Form.Item>
                                    </Col>
                                </>
                            ) : null}

                            <Col xs={24} md={24}>
                                <Form.Item label="Trạng thái">
                                    {getFieldDecorator('status', {
                                        rules: [{ required: false }],
                                        initialValue: currentItem?.status ?? null,
                                    })(
                                        <BaseRadioList
                                            options={paymentStatusOptions || []}
                                            optionValue="value"
                                            optionLabel="label"
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={24}>
                                <Form.Item label="Dịch vụ áp dụng">
                                    {getFieldDecorator('customer_service_ids', {
                                        rules: [{ required: false }],
                                        initialValue: initialServiceIds,
                                    })(
                                        <BaseSelect
                                            options={customer_services?.options || []}
                                            optionValue="id"
                                            optionLabel="label"
                                            showSearch
                                            mode='multiple'
                                            placeholder="Chọn một hoặc nhiều bản ghi"
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={24}>
                                <Form.Item label="Ghi chú">
                                    {getFieldDecorator("note", {
                                        rules: [{ required: false }],
                                        initialValue: _newLine(currentItem?.note) ?? null,
                                    })(
                                        <Input.TextArea rows={3} placeholder="Ghi chú" />
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                ) : null}
            </Drawer>
        );
    }
}

function mapStateToProps(state) {
    return {
        customer_services: state.customerServicesReducer.customer_services,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        createCustomerPayment: (data) => dispatch(createCustomerPayment(data)),
        updateCustomerPayment: (id, data) => dispatch(updateCustomerPayment(id, data)),
        getCustomerServices: (customer_id) => dispatch(getCustomerServices(customer_id)),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(
    Form.create({ name: 'CustomerPaymentForm' })(CustomerPaymentForm)
);